import React, { useEffect, useState, useRef, useMemo } from "react";
import { MultiSelect } from "react-multi-select-component";
import { DateRangePicker } from "react-date-range";
import { IBotAnalyticsFilterInput } from "../../utils/interfaces/IBotAnalyticsFilterInput";
import { usePlan } from "../../context/PlanContext";

type Props = {
  className: string;
  botAnalyticsFilterInput: IBotAnalyticsFilterInput;
  setBotAnalyticsFilterInput: React.Dispatch<
    React.SetStateAction<IBotAnalyticsFilterInput>
  >;
  allApplicationIds: { allApplicationId: { appName: string; id: number }[] };
  setGraphAnalyticsFilterInput: React.Dispatch<
    React.SetStateAction<IBotAnalyticsFilterInput>
  >;
};

const DashboardFilters: React.FC<Props> = ({
  className,
  botAnalyticsFilterInput,
  setBotAnalyticsFilterInput,
  allApplicationIds,
  setGraphAnalyticsFilterInput,
}) => {
  const { currentPlan } = usePlan();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Prepare dropdown options only when app list changes
  const options = useMemo(() => {
    return (
      allApplicationIds?.allApplicationId?.map((app) => ({
        label: app.appName,
        value: app.id,
      })) || []
    );
  }, [allApplicationIds]);

  // Selected Application IDs
  const [applicationIdSelected, setApplicationIdSelected] = useState<any[]>([]);

  // Format date as yyyy-MM-dd
  const formatDate = (date: Date) => date.toISOString().split("T")[0];

  const formatDisplayDate = (date: Date) =>
    date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // Date Range
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  // Sync initial state from props
  useEffect(() => {
    if (botAnalyticsFilterInput.startDate && botAnalyticsFilterInput.endDate) {
      setSelectionRange({
        key: "selection",
        startDate: new Date(botAnalyticsFilterInput.startDate),
        endDate: new Date(botAnalyticsFilterInput.endDate),
      });
    }

    if (allApplicationIds?.allApplicationId?.length > 0) {
      const selected = allApplicationIds.allApplicationId.map((app) => ({
        label: app.appName,
        value: app.id,
      }));
      setApplicationIdSelected(selected);

      const ids = selected.map((sel) => sel.value);
      setBotAnalyticsFilterInput((prev) => ({ ...prev, applicationIds: ids }));
      setGraphAnalyticsFilterInput((prev) => ({
        ...prev,
        applicationIds: ids,
      }));
    }
  }, [allApplicationIds]);

  // Update filters on application change
  useEffect(() => {
    const selectedIds = applicationIdSelected.map((app) => Number(app.value));
    if (selectedIds.length > 0) {
      setBotAnalyticsFilterInput((prev) => ({
        ...prev,
        applicationIds: selectedIds,
      }));
      setGraphAnalyticsFilterInput((prev) => ({
        ...prev,
        applicationIds: selectedIds,
      }));
    }
  }, [applicationIdSelected]);

  // Update filters on date change
  const handleSelect = (ranges: any) => {
    const { startDate, endDate } = ranges.selection;
    setSelectionRange(ranges.selection);

    setBotAnalyticsFilterInput((prev) => ({
      ...prev,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    }));
  };

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={{ position: "relative" }} className={className}>
      <div
        style={{
          display: "flex",
          gap: "16px",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {/* Date Picker */}
        <div style={{ position: "relative", minWidth: 250 }} ref={containerRef}>
          <label
            style={{
              position: "absolute",
              top: "-9px",
              left: "5px",
              background: "white",
              fontSize: 12,
              zIndex: 1,
            }}
          >
            Select date
          </label>
          <div
            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            style={{
              border: "1px solid #ccc",
              padding: "8px 12px",
              borderRadius: 4,
              cursor: "pointer",
              backgroundColor: "#fff",
              fontSize: 13,
              minHeight: 38,
            }}
          >
            {`${formatDisplayDate(
              selectionRange.startDate
            )} - ${formatDisplayDate(selectionRange.endDate)}`}
          </div>

          {isCalendarOpen && (
            <div
              ref={calendarRef}
              style={{
                position: "absolute",
                zIndex: 1000,
                padding: "5px",
                right: 0,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -6,
                  right: 20,
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderBottom: "6px solid white",
                }}
              />
              <DateRangePicker
                ranges={[selectionRange]}
                onChange={handleSelect}
                maxDate={new Date()}
                moveRangeOnFirstSelection={false}
                showMonthAndYearPickers={true}
              />
            </div>
          )}
        </div>

        {/* Application MultiSelect */}
        <div style={{ position: "relative", minWidth: 250 }}>
          <label
            style={{
              position: "absolute",
              top: "-9px",
              left: "5px",
              background: "white",
              fontSize: 12,
              zIndex: 1,
            }}
          >
            Application List
          </label>
          <MultiSelect
            options={options}
            value={applicationIdSelected}
            onChange={setApplicationIdSelected}
            labelledBy="Select"
            hasSelectAll={false}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardFilters;
